import { useEffect, useCallback, useState, useMemo } from 'react';
import styles from './CollectionEdit.module.scss';
import Header from '@components/header/Header';
import { useParams } from 'react-router-dom';
import Spinner from '@components/spinner/Spinner';
import { TextInput } from '@components/text-input/TextInput';
import RadioGroup from '@src/components/radio-group/RadioGroup';
import ArrowBottom from 'src/assets/icons/arrow_bottom.svg?react';
import IconPlus from 'src/assets/icons/plus_circle_filled.svg?react';
import TrashIcon from 'src/assets/icons/close.svg?react';
import {
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useSaveCollectionLessonsMutation,
  Collection,
  CollectionType
} from '@api/graphql-codegen';
import ImageUploadForm from '../../unit/edit/edit-content/image-form/ImageUploadForm';
import Select from '@src/components/select/Select';
import Button from '@src/components/button/Button';
import { useHistory } from 'react-router-dom';
import { useGetCollectionLazyQuery } from '@api/graphql-codegen';
import { EditSllLesson as EditSllLessonType, SelectOption } from '@src/types';
import { useToast } from '@src/hooks/useToast';
import type { DropResult, ResponderProvided, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from '@src/utils/componentCasts';
import { DragDropContext, Droppable, Draggable } from '@src/utils/componentCasts';
import {
  COLLECTION_TYPES, COLLECTION_FIELDS,
  COLLECTION_ERROR_MSGS
} from '@constants/collection';
import { Switch } from 'antd';
import { uploadFile } from '@src/services/File';
import {
  superAdminStandaloneLessonsAgeOptions,
  AustraliaStandaloneLessonAgeGroupOptions,
  NorthAmericaStandaloneLessonAgeGroupOptions
} from '@constants';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../../../utils/logger';
import FooterPage from '@src/components/footer-page/FooterPage';

export type EditSllLesson = EditSllLessonType;

export const options = [
  {label: 'Australia', value: 'Australia'},
  {label: 'Canada', value: 'Canada'},
  {label: 'USA', value: 'USA'},
  {label: 'Canada & USA', value: 'Canada & USA'},
  {label: 'All Countries', value: 'All Countries'}
];

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

type Params = { id: string; }

const defaultSubHeading = 'These Lessons follow the story of'

const defaultCollection: Partial<Collection> = {
  collectionType: COLLECTION_TYPES[0].value as CollectionType,
  name: '',
  isSubHeading: false,
  subHeading: defaultSubHeading,
  subHeadingHighlight: '',
  bannerImage: '',
  pacing: '',
  isSensitive: undefined,
  grade: '',
  country: '',
};

const defaultErrors = {
  collectionType: COLLECTION_ERROR_MSGS.COLLECTION_TYPE,
  name: COLLECTION_ERROR_MSGS.NAME,
  subHeading: COLLECTION_ERROR_MSGS.SUB_HEADING,
  bannerImage: COLLECTION_ERROR_MSGS.BANNER_IMAGE
}

function SuperCollectionEdit(props) {
  const { id: collectionId } = useParams<Params>();
  const [collection, setCollection] = useState<Partial<Collection>>(defaultCollection);
  const [subHeadingHighlight, setSubHeadingHighlight] = useState<string[]>([])
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>(defaultErrors)
  const [imageFile, setImageFIle] = useState<File>();
  const [localContents, setLocalContents] = useState<any | []>();
  const history = useHistory();
  const { successToast } = useToast();
  const { awsKeys } = useAWSKeys();

  const [createCollection, { loading: collectionCreateLoading }] = useCreateCollectionMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    }
  });

  const [updateCollection, { loading: collectionUpdateLoading }] = useUpdateCollectionMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    }
  });

  const [saveCollectionLessons] = useSaveCollectionLessonsMutation({
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong.');
    }
  });
  const [getCollection, { data: collectionFromDB, loading: collectionLoading, refetch: refetchLessons }] = useGetCollectionLazyQuery({
    variables: { input: { id: collectionId } }, fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (collectionId && collectionId !== 'new') {
      getCollection();
    }
  }, [getCollection, collectionId])

  useEffect(() => {
    if (collectionFromDB) {
      setCollection({ ...collectionFromDB.getCollection, subHeading: collectionFromDB.getCollection?.subHeading || defaultSubHeading });
      setSubHeadingHighlight(JSON.parse(collectionFromDB.getCollection?.subHeadingHighlight || '[]'))
    } else if (!collection) {
      setCollection(defaultCollection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionFromDB]);


  const handleCancel = () => {
    history.goBack();
  };

  const isFormValid = useMemo (() => {
    let errMsg = ''
    for (const k in errors) {
      errMsg += errors[k]
    }
    if (errMsg === '') return true
    return false
  }, [errors])

  useEffect(() => {
    setLocalContents(collection?.sllLessons);
  }, [collection])

  const handleDragEnd = useCallback(async (result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    const reorderedSubItems = reorder(
      localContents,
      sourceIndex,
      destIndex
    );
    setLocalContents(reorderedSubItems as any[]);
  }, [localContents, setLocalContents]);

  const handleAddLesson = () => {
    history.push(`/super/collections/customize/${collection?.id || 'new'}`);
  }

  const onChange = (key, value) => {
    if (key === COLLECTION_FIELDS.IS_SUB_HEADING) {
      setCollection((prev) => {
        return {
          ...prev,
          [key]: !prev.isSubHeading
        }
      })
    } else {
      setCollection((prev) => {
        return {
          ...prev,
          [key]: value
        }
      })
    }
  }

  const onUploadCollectionImage = (image: string, file: File | undefined) => {
    if (!file) {
      setCollection((prev) => {
        return {
          ...prev,
          bannerImage: ''
        }
      })
    }
    setImageFIle(file)
  }

  useEffect(() => {
    const isImageValid = () => {
      if (collection?.bannerImage) {
        return true
      }
      if (collection?.collectionType === CollectionType.COLLECTION_IMAGE && !imageFile) {
        return false
      }
      return true
    }
    const isSubHeadingValid = () => {
      if (!collection?.isSubHeading) return true
      if (collection?.isSubHeading && collection?.subHeading) return true
      return false
    }
    setErrors({
      collectionType: collection?.collectionType ? '' : COLLECTION_ERROR_MSGS.COLLECTION_TYPE,
      name: collection?.name ? '' : COLLECTION_ERROR_MSGS.NAME,
      subHeading: isSubHeadingValid() ?  '' : COLLECTION_ERROR_MSGS.SUB_HEADING,
      bannerImage: isImageValid() ? '' : COLLECTION_ERROR_MSGS.BANNER_IMAGE
    })
  }, [collection, imageFile])

  const onSave = async () => {
    setLoading(true)
    let bannerImage;
    const newSubHeadingHighlight = JSON.stringify(subHeadingHighlight.filter(v => v.trim().length > 0))
    setCollection((prev) => {
      return {
        ...prev,
        subHeadingHighlight: newSubHeadingHighlight
      }
    })
    if (imageFile) {
      bannerImage = await uploadFile(awsKeys, imageFile, 'images');
      setCollection((prev) => {
        return {
          ...prev,
          bannerImage: bannerImage
        }
      })
    }
    delete collection['__typename'];
    delete collection['sllLessons'];
    if (!collection.id) {
      const newCollection = await createCollection({
        variables: {
          input: {
            ...collection,
            isSensitive: !!collection.isSensitive,
            name: collection.name ?? '',
            bannerImage,
            subHeadingHighlight: newSubHeadingHighlight
          },
        }
      });
      const newCollectionnId = newCollection.data?.createCollection?.id;
      if (newCollectionnId) {
        successToast('Collection has been created successfully!');
        history.push(`/super/collections/edit/${newCollectionnId}`);
      } else {
        successToast('Failed to create a collection!');
      }
    } else {
      if (localContents?.length) {
        await saveCollectionLessons({
          variables: {
            input: {
              collectionId: collection.id,
              sllLessons: localContents?.map((i: any) => i?.id || '')
            }
          }
        });
      }
      const update = await updateCollection({
        variables: {
          input: {
            ...collection,
            id: collection.id,
            isSensitive: !!collection.isSensitive,
            name: collection.name ?? '',
            bannerImage,
            subHeadingHighlight: newSubHeadingHighlight
          },
        }
      });
      if (update.data?.updateCollection) {
        refetchLessons().then(() => {
          successToast('Collection has been updated successfully!');
        })
      } else {
        successToast('Failed to update a collection!');
      }
    }
    setLoading(false);
  }

  const handleAddSubHeadingHighlightInput = () => {
    setSubHeadingHighlight([...subHeadingHighlight, ''])
  }

  const handleRemoveSubHeadingHighlightInput = (index) => {
    const removed = subHeadingHighlight.filter((d, i) => i !== index)
    setSubHeadingHighlight(([...removed]))
  }

  const handleChangeSubHeadingHighlight = (value, index) => {
    const updated = subHeadingHighlight.map((v, i) => (i === index ? value : v))
    setSubHeadingHighlight([...updated])
  }

  if (collectionLoading) {
    return (
      <>
        <Header />
        <div className={styles.loadingBar}>
          <Spinner width={30} height={30} />
        </div>
      </>
    )
  }

  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.mainWrapper}>
          <div className={styles.list}>
            <div className={styles.programName}>Collection Overview Page</div>
            <div>
              <div className={styles.iconWrapper} onClick={() => { }}>
                <ArrowBottom className={styles.icon} />
                <label className={styles.sideBarTitle}>Lessons Presentation</label>
              </div>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="droppableContent" type="teacherModuleItem">
                  {(droppableProvided: DroppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                      style={{ display: 'flex', flexDirection: 'column', paddingLeft: '20px' }}
                    >
                      {localContents?.map((item: any, index: number) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={index}
                        >
                          {(
                            draggableProvided: DraggableProvided,
                            snapshot: DraggableStateSnapshot
                          ) => {
                            return (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}

                              >
                                <div {...draggableProvided?.dragHandleProps} className={snapshot.isDragging ? `${styles.lessonWrapper} ${styles.dragableItem}` : styles.lessonWrapper}>
                                  <div className={styles.circle} />
                                  <div key={item.id} {...draggableProvided?.dragHandleProps} className={styles.lessonLabel}>{item.name}</div>

                                </div>
                              </div>
                            );
                          }}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <div className={styles.addLessonWrapper}>
                <Button
                  className={styles.addLessonBtn} disabled={!!collectionId && collectionId === 'new'}
                  onClick={handleAddLesson} size={'small'} label={collectionId && collectionId === 'new' ? 'Add Lesson' : 'Edit Lessons'}
                />
              </div>
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.row}>
              <div className={styles.actions}>
                <div className={styles.title}>
                  Collection Overview
                </div>
                <div className={styles.buttons}>
                  <Button
                    className={styles.saveButton}
                    disabled={collectionCreateLoading || collectionUpdateLoading || !isFormValid || isLoading}
                    isLoading={collectionCreateLoading || collectionUpdateLoading || isLoading}
                    leftIcon={isLoading ? <Spinner width={20} height={20} /> : undefined}
                    onClick={onSave} size={'small'} label={'SAVE'}
                  />
                  <Button className={styles.cancelButton} disabled={collectionUpdateLoading || collectionCreateLoading || isLoading} onClick={handleCancel} size={'small'} label={'Cancel'} />
                </div>
              </div>
            </div>
            {collection ? (
              <>
                <div className={styles.row} style={{ marginTop: '40px' }}>
                  <div className={styles.subBoxTitle}>Select Collection Type</div>
                  <div className={styles.radioWrapper}>
                    <RadioGroup
                      value={collection.collectionType ?? ''}
                      options={COLLECTION_TYPES}
                      onChange={(value) => onChange(COLLECTION_FIELDS.COLLECTION_TYPE, value)}
                      name={''}
                      hasError={!collection.collectionType && !!errors?.collectionType}
                      subTitle={!collection.collectionType && errors?.collectionType}
                      style={{ display: 'flex', gap: '1rem' }}
                    />
                  </div>
                </div>
                <div className={styles.row} style={{ marginTop: '40px' }}>
                  <div className={`${styles.uploadertitle}`}>* Name</div>
                  <TextInput
                    hasError={!!errors?.name}
                    subTitle={errors?.name}
                    label={''}
                    id={'name'}
                    placeholder={'Add collection name'}
                    value={collection?.name ?? ''}
                    onChange={(value) => onChange(COLLECTION_FIELDS.NAME, value)}
                  />
                </div>
                <div className={styles.row} style={{ marginTop: '40px' }}>
                  <div className={styles.uploadertitle}>Collection Country</div>
                  <RadioGroup
                    value={collection.country || 'All Countries'}
                    options={options}
                    onChange={(value: string) => onChange(COLLECTION_FIELDS.COUNTRY, value)}
                    name={''}
                    hasError={false}
                    subTitle={''}
                  />
                </div>
                <div className={styles.row} style={{ marginTop: '40px' }}>
                  <div className={styles.uploadertitle}>Age group</div>
                  <Select
                    options={collection.country === options[0].value ?
                      AustraliaStandaloneLessonAgeGroupOptions :
                      (collection.country === options[1].value
                        || collection.country === options[2].value
                        || collection.country === options[3].value ?
                        NorthAmericaStandaloneLessonAgeGroupOptions :
                        superAdminStandaloneLessonsAgeOptions
                      )
                    }
                    value={collection.grade}
                    onChange={(value: SelectOption) => onChange(COLLECTION_FIELDS.GRADE, value.value)}
                    doNotSort={true}
                    inDropDown={true}
                  />
                </div>
                <div className={styles.row} style={{ marginTop: '40px' }}>
                  <div className={styles.subHeadingToggle} onClick={(value) => onChange(COLLECTION_FIELDS.IS_SUB_HEADING, value)}>
                    <Switch checked={collection?.isSubHeading ?? false} /><span>{'Collection Sub Heading'}</span>
                  </div>
                  {collection?.isSubHeading && <>
                    <div className={`${styles.uploadertitle}`}>* Sub Heading</div>
                    <TextInput
                      hasError={!!errors?.subHeading}
                      subTitle={errors?.subHeading}
                      label={''}
                      id={'name'}
                      placeholder={'Add sub heading'}
                      value={collection?.subHeading ?? ''}
                      onChange={(value) => onChange(COLLECTION_FIELDS.SUB_HEADING, value)}
                    />
                    <div className={`${styles.uploadertitle}`}>* Sub Heading Highlights</div>
                    {subHeadingHighlight.map((v, index) => (
                      <div key={index} className={`${styles.option}`}>
                        <button className={styles.removeButton} onClick={() => handleRemoveSubHeadingHighlightInput(index)}>
                          <TrashIcon stroke='#999999' className={styles.iconRemove}/>
                        </button>
                        <div className={styles.questionBox}>
                          <TextInput
                            label={''}
                            id={'subHeadingHighlight'}
                            placeholder={'Add sub heading highlights'}
                            value={v ?? ''}
                            onChange={(value) => handleChangeSubHeadingHighlight(value, index)}
                          />
                        </div>
                      </div>
                    ))}
                    <button className={styles.plusButton} onClick={() => handleAddSubHeadingHighlightInput()}>
                      <IconPlus className={styles.iconPlus}/>
                    </button>
                  </>}
                </div>
                <div className={styles.row}>
                  <div className={styles.uploadertitle}>
                    {collection?.collectionType === CollectionType.COLLECTION_IMAGE ? '*' : ''} Collection Image
                  </div>
                  <ImageUploadForm
                    url={collection.bannerImage || ''}
                    onChange={onUploadCollectionImage}
                    hasError={!!errors?.bannerImage}
                    subTitle={errors?.bannerImage}
                  />
                </div>
                <hr className={styles.horizDivider} />
                <div className={`${styles.row} ${styles.w15}`} style={{ marginTop: '40px' }}>
                  <div className={`${styles.uploadertitle}`}>Pacing</div>
                  <TextInput
                    hasError={!!errors?.pacing}
                    subTitle={errors?.pacing}
                    label={''}
                    id={'pacing'}
                    placeholder={'3 x 30'}
                    value={collection?.pacing ?? ''}
                    onChange={(value) => onChange(COLLECTION_FIELDS.PACING, value)}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
        <FooterPage />
      </div>
    </>
  );
}

const CollectionEdit = () => {
  return (
    <SuperCollectionEdit />
  )
};

export default CollectionEdit;
